/**
 * Reports Hooks
 *
 * Provides hooks for report data fetching with TanStack Query.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import type {
  CustomerSatisfactionData,
  SatisfactionTrend,
  AgentPerformanceData,
  AgentPerformanceTrend,
} from '@/types';

// API response types

/**
 * One bucket of invoiced revenue (cents, by tenant-local invoice date).
 * `labor` and `parts` come from the invoices' frozen lines; `other` is the rest
 * (service, surcharge and discount lines, invoices without lines), so
 * labor + parts + other = revenue. `workOrders` counts work orders completed
 * in the bucket.
 */
export interface RevenueDataPoint {
  date: string;
  revenue: number;
  labor: number;
  parts: number;
  other: number;
  workOrders: number;
}

export interface ReportSummary {
  totalRevenue: number;
  revenueChange: number;
  workOrdersCompleted: number;
  workOrdersChange: number;
  avgTicketValue: number;
  avgTicketChange: number;
  laborHours: number;
  laborHoursChange: number;
}

export interface MechanicPerformanceItem {
  mechanicId: string;
  name: string;
  workOrders: number;
  revenue: number;
  laborHours: number;
  /**
   * Nominal workday utilization: logged hours / 8 h workdays, capped at 100.
   * NOT task efficiency (#3857); kept under this name for the mobile contract.
   */
  efficiency: number;
  /** Same number as `efficiency`, under its truthful name. */
  utilizationPercent?: number;
  /** Matched billable / actual hours as a decimal ratio (uncapped); null when unknown or hidden. */
  billableEfficiency?: number | null;
  /** Tasks behind `billableEfficiency`; null when billing data is hidden. */
  billableEfficiencyTasks?: number | null;
  /** Tasks this mechanic logged time on. */
  timedTasks?: number;
}

export interface ServiceTypeStatItem {
  name: string;
  count: number;
  revenue: number;
  avgDuration: number;
}

export interface MonthlyComparisonItem {
  month: string;
  current: number;
  previous: number;
}

/**
 * Invoiced revenue (cents) that no row of the breakdown can claim — revenue with
 * no work order or no mechanic (mechanic / service-type tables), or no nameable
 * customer (customer stats). For the mechanic and service-type tables, the full
 * rows + this = the report's total revenue. `topCustomers` is only the top 5, so
 * with more than 5 known customers sum(topCustomers) + this is less than the
 * total.
 */
export interface UnattributedRevenue {
  unattributedRevenue?: number;
}

export interface CustomerStats extends UnattributedRevenue {
  newCustomers: number;
  returningCustomers: number;
  totalCustomers: number;
  avgVisitsPerCustomer: number;
  topCustomers: { name: string; revenue: number; visits: number }[];
}

// Hooks

export function useRevenueReport(dateFrom: string, dateTo: string, groupBy: string = 'day') {
  return useQuery({
    queryKey: queryKeys.reports.revenue({ dateFrom, dateTo, groupBy }),
    queryFn: () =>
      api.get<{ data: RevenueDataPoint[] }>('/reports/revenue', {
        params: { dateFrom, dateTo, groupBy },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

export function useReportSummary(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: queryKeys.reports.summary({ dateFrom, dateTo }),
    queryFn: () =>
      api.get<ReportSummary>('/reports/summary', {
        params: { dateFrom, dateTo },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

export function useMechanicPerformance(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: queryKeys.reports.mechanicPerformance({ dateFrom, dateTo }),
    queryFn: () =>
      api.get<{ data: MechanicPerformanceItem[] } & UnattributedRevenue>('/reports/mechanic-performance', {
        params: { dateFrom, dateTo },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

export function useServiceTypeStats(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: queryKeys.reports.serviceTypes({ dateFrom, dateTo }),
    queryFn: () =>
      api.get<{ data: ServiceTypeStatItem[] } & UnattributedRevenue>('/reports/service-types', {
        params: { dateFrom, dateTo },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

export function useMonthlyComparison(year?: number) {
  return useQuery({
    queryKey: queryKeys.reports.monthlyComparison(year),
    queryFn: () =>
      api.get<{ data: MonthlyComparisonItem[] }>('/reports/monthly-comparison', {
        params: year ? { year } : {},
      }),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCustomerStats(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: queryKeys.reports.customerStats({ dateFrom, dateTo }),
    queryFn: () =>
      api.get<CustomerStats>('/reports/customer-stats', {
        params: { dateFrom, dateTo },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

export interface CustomerSatisfactionReport extends CustomerSatisfactionData {
  trend: SatisfactionTrend[];
}

export function useCustomerSatisfaction(dateFrom: string, dateTo: string, groupBy = 'day') {
  return useQuery({
    queryKey: queryKeys.reports.customerSatisfaction({ dateFrom, dateTo, groupBy }),
    queryFn: () =>
      api.get<CustomerSatisfactionReport>('/reports/customer-satisfaction', {
        params: { dateFrom, dateTo, groupBy },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

export interface AgentPerformanceReport extends AgentPerformanceData {
  trend: AgentPerformanceTrend[];
}

export function useAgentPerformance(dateFrom: string, dateTo: string, groupBy = 'day') {
  return useQuery({
    queryKey: queryKeys.reports.agentPerformance({ dateFrom, dateTo, groupBy }),
    queryFn: () =>
      api.get<AgentPerformanceReport>('/reports/agent-performance', {
        params: { dateFrom, dateTo, groupBy },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}

/**
 * Task counts behind the ratios (#3857). A task is a booking service, not a
 * work order. `efficiencyTasks` is null when the caller may not see billing data.
 */
export interface DurationCoverage {
  /** Tasks in scope (for a mechanic: tasks they logged time on). */
  tasks: number;
  /** Tasks with actual hours > 0. */
  timedTasks: number;
  /** Tasks with actual > 0 and billable > 0; null when billing is hidden. */
  efficiencyTasks: number | null;
  /** Tasks with actual > 0 and planned > 0. */
  planningTasks: number;
}

/** Decimal hours and ratios from GET /reports/work-order-duration-performance (#2707). */
export interface DurationPerformanceMetrics {
  /** Total planned hours over every task in scope (not only the ratio cohort). */
  plannedHours: number;
  /** Total known billable hours; null when unknown (fixed price, no labor items) or hidden. */
  billableHours: number | null;
  /** Total actual hours (not only the ratio cohort). */
  actualHours: number;
  /** Matched billable / actual; null without an eligible cohort or billing access. */
  efficiency: number | null;
  /** Matched planned / actual; null without an eligible cohort. */
  planningAccuracy: number | null;
  /** Numerator and denominator behind `efficiency`; null when billing is hidden. */
  efficiencyBasis: { billableHours: number; actualHours: number } | null;
  /** Numerator and denominator behind `planningAccuracy`. */
  planningBasis: { plannedHours: number; actualHours: number };
  coverage: DurationCoverage;
}

export interface MechanicDurationPerformanceRow extends DurationPerformanceMetrics {
  mechanicId: string | null;
  name: string | null;
}

export interface ServiceDurationPerformanceRow extends DurationPerformanceMetrics {
  serviceId: string | null;
  name: string | null;
}

export interface WorkOrderDurationPerformanceReport {
  /** False when the caller may not see billing data. */
  billableVisible: boolean;
  summary: DurationPerformanceMetrics;
  mechanics: MechanicDurationPerformanceRow[];
  services: ServiceDurationPerformanceRow[];
}

export function useWorkOrderDurationPerformance(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: queryKeys.reports.workOrderDurationPerformance({ dateFrom, dateTo }),
    queryFn: () =>
      api.get<WorkOrderDurationPerformanceReport>('/reports/work-order-duration-performance', {
        params: { dateFrom, dateTo },
      }),
    enabled: !!dateFrom && !!dateTo,
    staleTime: 60 * 1000,
  });
}
